// useCharacterContext.js
// -----------------------------------------------------------------------------
// Resolver seam for the Character Context Hover Panel.
//
// Given (bookUuid, characterUuid) — or (characterName, bookTitle) for the
// mockup-only demo route — returns the panel data structure consumed by
// `HoverPanelMockup` (see mockCharacters.js for the shape).
//
// Phase 4D — Chunk 6: live-fetch flip.
//   - When `bookUuid` AND `characterId` are both present, the hook calls the
//     server's GET /api/character-context/:bookUuid/:characterUuid endpoint
//     (added by Chunk 5) and runs the envelope through
//     `envelopeToPanelShape.js` (Chunk 6 adapter).
//   - When either is missing — i.e. the standalone demo route at
//     /dev/hover-panel-mockup, or any host page that still mounts a
//     CharacterContextTrigger without a bookUuid — the hook keeps the
//     original fixture-cycling behaviour. This preserves the design demo
//     surface untouched.
//
// Return shape (object):
//   { characterContext, loading, error }
//
// `characterContext` is null on initial mount until the fetch settles or the
// mock branch returns. Callers (CharacterContextPopover) decide what to do
// with the loading / error transitions.
//
// In-memory client-side cache keyed by `bookUuid:characterUuid` keeps the
// second open of the same character instant. The server already LRU-caches
// the envelope; this is purely a network-suppress layer. Cache lifetime is
// the SPA tab lifetime.
//
// Spec reference: PHASE_4D_VA_OPPORTUNITY_CHARACTER_HOVER_PANEL_DESIGN.md §8
//                 PHASE_4D_HOVER_PANEL_SERVER_SIDE_IMPLEMENTATION_PLAN_2026-05-17.md §3 Chunk 6
// -----------------------------------------------------------------------------

import { useEffect, useMemo, useRef, useState } from 'react';
import mockCharacters from '../components/characterContext/HoverPanelMockup/mockCharacters';
import { apiClient } from '../apiTools/apiClient';
import { config } from '../constants/config';
import { envelopeToPanelShape } from '../utils/characterContext/envelopeToPanelShape';

// ---------------------------------------------------------------------------
// Live-fetch master toggle. Set to false to force the mock branch even when
// bookUuid is present (useful for one-line rollback if the server endpoint
// regresses). The host components don't change.
// ---------------------------------------------------------------------------
const USE_REAL_DATA = true;

// ---------------------------------------------------------------------------
// Tiny tab-local cache. Keyed `${bookUuid}:${characterUuid}`. Values are the
// already-adapted panel-shape object. The server's LRU dedupes upstream
// composition; this dedupes the network call itself.
// ---------------------------------------------------------------------------
const _panelCache = new Map();
const PANEL_CACHE_MAX = 64;

function _cacheGet(key) {
  if (!_panelCache.has(key)) return undefined;
  const v = _panelCache.get(key);
  _panelCache.delete(key);
  _panelCache.set(key, v); // refresh recency
  return v;
}

function _cacheSet(key, value) {
  if (_panelCache.has(key)) _panelCache.delete(key);
  _panelCache.set(key, value);
  if (_panelCache.size > PANEL_CACHE_MAX) {
    const oldest = _panelCache.keys().next();
    if (!oldest.done) _panelCache.delete(oldest.value);
  }
}

// Exposed for tests / debugging — never used by production callers.
export function _resetPanelCache() {
  _panelCache.clear();
}

// ---------------------------------------------------------------------------
// Mockup-phase resolver: hash the character name → pick one of the four
// fixtures. Same name always returns the same fixture. The fixture's own
// `name` field is overwritten with the live opportunity character's name.
// ---------------------------------------------------------------------------
function hashString(s) {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h >>> 0;
}

function resolveMockCharacterContext(characterName, bookTitle) {
  const fallbackName = '(unnamed character)';
  const name = (characterName && characterName.trim()) || fallbackName;
  const idx = hashString(name.toLowerCase()) % mockCharacters.length;
  const template = mockCharacters[idx];
  return {
    ...template,
    name,
    ...(bookTitle ? { bookTitle } : {}),
  };
}

// ---------------------------------------------------------------------------
// Live resolver. Fetches the envelope from the Chunk-5 endpoint and adapts
// it. Returns the panel-shape object directly. Errors propagate to the
// caller (the hook catches them and surfaces via the `error` field).
// ---------------------------------------------------------------------------
async function fetchAndAdapt({ bookUuid, characterUuid, bookTitle, signal }) {
  const resp = await apiClient.get(
    config.GET_CHARACTER_CONTEXT,
    { bookUuid, characterUuid },
    {},
    { signal }
  );
  const envelope = resp && resp.data ? (resp.data.data || resp.data) : null;
  if (!envelope) {
    throw new Error('useCharacterContext: empty envelope from server');
  }
  return envelopeToPanelShape(envelope, { bookTitle });
}

/**
 * Resolve a character-context shape for HoverPanelMockup.
 *
 * @param {object} args
 * @param {string} [args.bookUuid]       Owning book UUID. REQUIRED for the
 *                                        live-fetch branch. When absent, the
 *                                        mock fixture branch runs.
 * @param {string} [args.characterId]    Character UUID. REQUIRED for the
 *                                        live-fetch branch (alongside bookUuid).
 * @param {string} [args.characterName]  Display name. Used by the mock branch
 *                                        for deterministic cycling, and as a
 *                                        fallback header when the live envelope
 *                                        is still loading or returns blank.
 * @param {string} [args.bookTitle]      Book title overlay. Always carried
 *                                        through both branches.
 * @returns {{ characterContext: object|null, loading: boolean, error: Error|null }}
 */
export default function useCharacterContext({
  bookUuid,
  characterId,
  characterName,
  bookTitle,
} = {}) {
  const canFetchLive =
    USE_REAL_DATA && !!bookUuid && !!characterId && typeof bookUuid === 'string' && typeof characterId === 'string';

  // Mock branch — synchronous via useMemo. Used when canFetchLive is false.
  const mockContext = useMemo(() => {
    if (canFetchLive) return null;
    if (!characterId && !characterName) return null;
    return resolveMockCharacterContext(characterName, bookTitle);
  }, [canFetchLive, characterId, characterName, bookTitle]);

  // Live branch state.
  const [liveContext, setLiveContext] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Track the last-issued request so a slow earlier fetch can't overwrite
  // a faster later one when the caller toggles characters quickly.
  const reqIdRef = useRef(0);

  useEffect(() => {
    if (!canFetchLive) {
      // Drop any prior live state when we fall back to mock; prevents a
      // stale full-card from flashing in over the mock fixture.
      setLiveContext(null);
      setLoading(false);
      setError(null);
      return undefined;
    }

    const cacheKey = `${bookUuid}:${characterId}`;
    const cached = _cacheGet(cacheKey);
    if (cached) {
      // Hydrate from cache; still re-apply the current bookTitle in case the
      // host changed it (different opportunity, same character).
      setLiveContext({ ...cached, ...(bookTitle ? { bookTitle } : {}) });
      setLoading(false);
      setError(null);
      return undefined;
    }

    const controller =
      typeof AbortController !== 'undefined' ? new AbortController() : null;
    const reqId = ++reqIdRef.current;

    setLoading(true);
    setError(null);

    fetchAndAdapt({
      bookUuid,
      characterUuid: characterId,
      bookTitle,
      signal: controller ? controller.signal : undefined,
    })
      .then((shape) => {
        if (reqIdRef.current !== reqId) return; // stale
        if (shape) _cacheSet(cacheKey, shape);
        setLiveContext(shape);
        setLoading(false);
      })
      .catch((err) => {
        if (reqIdRef.current !== reqId) return; // stale
        // axios marks aborted requests with `code === 'ERR_CANCELED'` or a
        // `name === 'CanceledError'`. Suppress those — they are not errors.
        if (
          err &&
          (err.name === 'CanceledError' ||
            err.code === 'ERR_CANCELED' ||
            err.name === 'AbortError')
        ) {
          return;
        }
        // eslint-disable-next-line no-console
        console.warn('[useCharacterContext] live fetch failed:', err);
        setLiveContext(null);
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
      });

    return () => {
      if (controller) controller.abort();
    };
  }, [canFetchLive, bookUuid, characterId, bookTitle]);

  if (canFetchLive) {
    return { characterContext: liveContext, loading, error };
  }
  return { characterContext: mockContext, loading: false, error: null };
}
